import React from 'react';
import { Nullable } from 'global';
import { QuoteCalcViewModel } from 'modules/quote';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { Column } from 'components/shared/TableListing/types';
import { DeleteOutline, InfoOutlined } from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { removeGraphQLMEtaFields } from 'utils/removeGraphQLMEtaFields';
import { EquipmentDelivery, equipmentConditions } from 'modules/equipment';
import { FinanceProgramFormValues, CcEmailOptionGroups } from 'modules/desking/types';
import {
  ChargePriceInput,
  DiscountType,
  QuoteDepositType,
  AftermarketProductCategory,
} from '@trnsact/trnsact-shared-types';
import { CurrencyInputField, InputField, NumberFormatField, SelectField } from 'components/form/rhf';
import type { QuoteEquipmentResponse, QuoteEquipmentTradeInsResponse } from '@trnsact/trnsact-shared-types';

import { NetTradeCell, OverOrUnderAllowanceCell } from '../ui/steps/pricing/tradeInTable';
import {
  AcceptedMenuProduct,
  AcceptedPaymentCriteriaSnapshot,
  CustomerContactItem,
  DealBuilderQuote,
  InventoryEquipmentItem,
} from '../api';
import { MAX_ADDITIONAL_RECIPIENTS, STEP_CONFIG, TRADE_IN_YEAR_OPTIONS } from '../constants';
import {
  CalculateDealBuilderSummaryInput,
  DealBuilderContact,
  DealBuilderContactsData,
  DealBuilderEquipmentSource,
  DealBuilderFormValues,
  DealBuilderStep,
  DealBuilderSummary,
  SendEmailOption,
  TradeInListingConfig,
  TradeInListingItem,
} from '../types';

export const getStepIndex = (step: DealBuilderStep): number => STEP_CONFIG.findIndex(config => config.step === step);

// Avoids float drift (e.g. 3899.7799999999997) from repeated addition/subtraction of currency values
export const roundToCents = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const calculateDealBuilderSummary = ({
  hardCost,
  feesAndTaxes,
  depositValue,
  depositType,
  tradeIn,
}: CalculateDealBuilderSummaryInput): DealBuilderSummary => {
  const customerDeposit =
    depositType === QuoteDepositType.Percentage
      ? ((depositValue ?? 0) / 100) * (hardCost + feesAndTaxes)
      : (depositValue ?? 0);

  const balanceDue = hardCost + feesAndTaxes - customerDeposit - tradeIn;

  return { hardCost, feesAndTaxes, customerDeposit, tradeIn, balanceDue };
};

export const mapQuoteCalcToSummary = (priceCalc: Nullable<QuoteCalcViewModel>): DealBuilderSummary => {
  const net = priceCalc?.netFinanceAmount;

  return {
    hardCost: net?.hardCost ?? 0,
    tradeIn: net?.netTradeIn ?? 0,
    customerDeposit: net?.deposit ?? 0,
    balanceDue: net?.balanceToFinance ?? 0,
    feesAndTaxes: roundToCents((net?.fees ?? 0) + (net?.taxes ?? 0)),
  };
};

export const calculateNetTrade = ({
  tradeInValue,
  amountOwed,
}: Pick<TradeInListingItem, 'tradeInValue' | 'amountOwed'>): number =>
  roundToCents((tradeInValue ?? 0) - (amountOwed ?? 0));

export const tradeInListingColumns: Column<TradeInListingItem, TradeInListingConfig>[] = [
  {
    header: 'Make',
    renderKey: 'make',
    width: 'minmax(150px, 1fr)',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <InputField
        label="Make"
        control={form.control}
        name={`pricing.tradeIn.${rowIndex}.make`}
        inputProps={{
          size: 'small',
          variant: 'outlined',
        }}
      />
    ),
  },
  {
    header: 'Model',
    renderKey: 'model',
    width: 'minmax(150px, 1fr)',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <InputField
        label="Model"
        control={form.control}
        name={`pricing.tradeIn.${rowIndex}.model`}
        inputProps={{
          size: 'small',
          variant: 'outlined',
        }}
      />
    ),
  },
  {
    header: 'Year',
    renderKey: 'year',
    width: 'minmax(150px, 1fr)',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <SelectField
        label="Year"
        variant="outlined"
        control={form.control}
        options={TRADE_IN_YEAR_OPTIONS}
        name={`pricing.tradeIn.${rowIndex}.year`}
      />
    ),
  },
  {
    header: 'Condition',
    renderKey: 'newUsed',
    width: 'minmax(150px, 1fr)',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <SelectField
        variant="outlined"
        control={form.control}
        options={equipmentConditions}
        name={`pricing.tradeIn.${rowIndex}.newUsed`}
      />
    ),
  },
  {
    header: 'Serial/VIN#',
    renderKey: 'serialNumberVin',
    width: 'minmax(150px, 1fr)',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <InputField
        label="Serial/VIN#"
        control={form.control}
        name={`pricing.tradeIn.${rowIndex}.serialNumberVin`}
        inputProps={{
          size: 'small',
          variant: 'outlined',
        }}
      />
    ),
  },
  {
    header: 'Mileage',
    renderKey: 'mileage',
    width: '150px',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <NumberFormatField
        label="Mileage"
        control={form.control}
        name={`pricing.tradeIn.${rowIndex}.mileage`}
        textFieldProps={{
          size: 'small',
          variant: 'outlined',
        }}
      />
    ),
  },
  {
    header: 'ACV',
    renderHeadCell: () => (
      <Stack direction="row" gap="0.2rem" alignItems="center">
        <Tooltip
          arrow
          placement="bottom-start"
          title={
            <Stack gap="0.25rem">
              <Typography variant="subtitle2" component="span">
                Actual Cash Value
              </Typography>

              <Typography variant="caption" component="span">
                The current wholesale market value of the trade-in — what the dealer would realistically pay or receive
                for it today, before any trade-in allowance.
              </Typography>
            </Stack>
          }
        >
          <InfoOutlined color="primary" fontSize="small" />
        </Tooltip>

        <Typography variant="subtitle2" component="span">
          ACV
        </Typography>
      </Stack>
    ),
    renderKey: 'actualCashValue',
    width: '150px',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <CurrencyInputField
        label="ACV"
        control={form.control}
        customOnChange={nextValue => {
          form.setValue(`pricing.tradeIn.${rowIndex}.actualCashValue`, nextValue ?? 0);

          const tradeInValue = form.getValues(`pricing.tradeIn.${rowIndex}.tradeInValue`);

          form.setValue(
            `pricing.tradeIn.${rowIndex}.overUnderAllowance`,
            roundToCents((tradeInValue ?? 0) - (nextValue ?? 0))
          );
        }}
        name={`pricing.tradeIn.${rowIndex}.actualCashValue`}
        textFieldProps={{
          size: 'small',
          variant: 'outlined',
        }}
      />
    ),
  },
  {
    header: 'Trade-in Value',
    renderKey: 'tradeInValue',
    width: '150px',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <CurrencyInputField
        label="Trade-in"
        control={form.control}
        customOnChange={nextValue => {
          form.setValue(`pricing.tradeIn.${rowIndex}.tradeInValue`, nextValue ?? 0);

          const actualCashValue = form.getValues(`pricing.tradeIn.${rowIndex}.actualCashValue`);

          form.setValue(
            `pricing.tradeIn.${rowIndex}.overUnderAllowance`,
            roundToCents((nextValue ?? 0) - (actualCashValue ?? 0))
          );
        }}
        name={`pricing.tradeIn.${rowIndex}.tradeInValue`}
        textFieldProps={{
          size: 'small',
          variant: 'outlined',
        }}
      />
    ),
  },
  {
    header: 'Over/Under',
    renderHeadCell: () => (
      <Stack direction="row" gap="0.2rem" alignItems="center">
        <Tooltip
          arrow
          placement="bottom-start"
          title={
            <Stack gap="0.25rem">
              <Typography variant="subtitle2" component="span">
                Over/Under Allowance
              </Typography>

              <Typography variant="caption" component="span">
                Allowance = Trade-in Value − ACV
              </Typography>
            </Stack>
          }
        >
          <InfoOutlined color="primary" fontSize="small" />
        </Tooltip>

        <Typography variant="subtitle2" component="span">
          Over/Under
        </Typography>
      </Stack>
    ),
    renderKey: 'overUnderAllowance',
    width: '10rem',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <OverOrUnderAllowanceCell rowIndex={rowIndex} control={form.control} />
    ),
  },
  {
    header: 'Amount Owed',
    renderKey: 'amountOwed',
    width: 'minmax(150px, 1fr)',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => (
      <CurrencyInputField
        label="Amount"
        control={form.control}
        name={`pricing.tradeIn.${rowIndex}.amountOwed`}
        textFieldProps={{
          size: 'small',
          variant: 'outlined',
        }}
      />
    ),
  },
  {
    header: 'Net Trade',
    renderKey: 'netTrade',
    width: 'minmax(120px, 1fr)',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { form } }) => <NetTradeCell control={form.control} rowIndex={rowIndex} />,
  },
  {
    header: 'Action',
    renderKey: 'action',
    width: '80px',
    headerStyles: {
      minHeight: 'auto',
    },
    renderCell: ({ rowIndex, context: { onRemoveRow } }) => (
      <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
        <IconButton size="small" onClick={() => onRemoveRow(rowIndex)} aria-label="Remove trade-in row">
          <DeleteOutline fontSize="small" color="error" />
        </IconButton>
      </Stack>
    ),
  },
];

export const isStepUnlocked = (step: DealBuilderStep, progressStep: DealBuilderStep): boolean =>
  getStepIndex(step) <= getStepIndex(progressStep);

export const getNextStep = (step: DealBuilderStep): DealBuilderStep =>
  STEP_CONFIG[Math.min(getStepIndex(step) + 1, STEP_CONFIG.length - 1)].step;

export const getPrevStep = (step: DealBuilderStep): DealBuilderStep =>
  STEP_CONFIG[Math.max(getStepIndex(step) - 1, 0)].step;

const mapDealBuilderContact = (contact: CustomerContactItem): DealBuilderContact => ({
  ownerPgId: contact.ownerPgDetails?.ownerPgId ?? null,
  accountContactId: contact.accountContactDetails?.id ?? null,
  fullName: [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unknown Contact',
  title: contact.title ?? null,
  email: contact.email ?? contact.accountContactDetails?.email ?? null,
  phone: contact.phone ?? null,
});

export const getContactId = (contact: DealBuilderContact): string =>
  contact.ownerPgId ?? contact.accountContactId ?? '';

const isPrimaryContact = (contact: CustomerContactItem, creditAppId: Nullable<string>): boolean =>
  contact.contactType === 'OWNER_PG' &&
  !!contact.ownerPgDetails?.pc &&
  !!creditAppId &&
  contact.ownerPgDetails?.creditApplicationId === creditAppId;

export const collectDealBuilderContactsData = (
  vo: Nullable<VendorOpportunity>,
  contacts: CustomerContactItem[],
  creditAppId: Nullable<string>
): DealBuilderContactsData => {
  const account = vo?.potentialCustomer
    ? { name: vo.potentialCustomer.name ?? null, address: vo.potentialCustomer.address ?? null }
    : null;

  const allContacts = contacts.map(mapDealBuilderContact);

  const primaryRaw = contacts.find(c => isPrimaryContact(c, creditAppId));
  const primaryContact = primaryRaw ? mapDealBuilderContact(primaryRaw) : null;

  const additionalRecipients = contacts
    .filter(c => !isPrimaryContact(c, creditAppId))
    .slice(0, MAX_ADDITIONAL_RECIPIENTS)
    .map(mapDealBuilderContact);

  return { account, primaryContact, additionalRecipients, allContacts };
};

export const getContactLabel = (contact: SendEmailOption, includeEmail = false): string => {
  const label = `${contact.fullName}${contact.title ? `, ${contact.title}` : ''}`;
  return includeEmail ? `${label} - ${contact.email}` : label;
};

export const getContactInitials = (fullName: string): string =>
  fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');

export const isSameDeliveryAddress = (a: EquipmentDelivery | null, b: EquipmentDelivery) =>
  !!a &&
  a.streetAddress === b.streetAddress &&
  a.city === b.city &&
  a.state === b.state &&
  a.postalCode === b.postalCode &&
  a.country === b.country;

export const mapEquipmentSourceToItem = (source: DealBuilderEquipmentSource): InventoryEquipmentItem => {
  const name = [source.year, source.make, source.model].filter(Boolean).join(' ');

  return {
    id: source.equipmentId,
    equipment: {
      id: source.equipmentId,
      assets: source.assets,
      price: {
        retailPrice: source.retailPrice ?? null,
        wholesaleCost: source.wholesaleCost ?? null,
        totalWholesaleCost: null,
      },
      details: {
        make: source.make ?? '',
        vin: source.serialNumberVin ?? '',
        year: source.year ?? '',
        model: source.model ?? '',
        name,
        class: '',
        mileage: source.mileage != null ? String(source.mileage) : '',
        stockId: source.stockId ?? '',
        condition: source.newUsed ?? '',
        description: source.additionalDescription ?? '',
      },
    },
  };
};

// The default (no markup/discount/tax) equipment price used when the equipment is first attached to the quote
const buildDefaultChargePrice = (retailPrice: number, wholesalePrice: number): ChargePriceInput => ({
  retailPrice,
  wholesalePrice,
  netPrice: retailPrice,
  total: retailPrice,
  markup: 0,
  markupType: DiscountType.Percent,
  discount: 0,
  discountType: DiscountType.Percent,
  salesTax: 0,
  salesTaxType: DiscountType.Percent,
});

export const mapEquipmentItemToUpdateQuoteEquipmentInput = (item: InventoryEquipmentItem) => {
  const retailPrice = item.equipment.price.retailPrice ?? 0;
  const wholesalePrice = item.equipment.price.wholesaleCost ?? 0;

  return {
    externalId: '',
    attributes: [],
    cost: retailPrice,
    equipmentId: item.equipment.id,
    vin: item.equipment.details.vin,
    stockNumber: item.equipment.details.stockId,
    price: buildDefaultChargePrice(retailPrice, wholesalePrice),
  };
};

export const mapQuoteToUpdateQuoteInput = (
  quote: Nullable<DealBuilderQuote>,
  equipmentEntry: ReturnType<typeof mapEquipmentItemToUpdateQuoteEquipmentInput>,
  vendorOpportunityId: string
): Record<string, unknown> =>
  removeGraphQLMEtaFields({
    vendorOpportunityId,
    term: null,
    downPayment: null,
    interestRate: null,
    paymentAmount: null,
    contractStartDate: null,
    firstPaymentDueDate: null,
    fees: quote?.fees ?? [],
    equipmentTradeIns: quote?.equipmentTradeIns ?? [],
    deposit: quote?.deposit ?? null,
    attributes: quote?.attributes ?? [],
    aftermarketProducts: quote?.aftermarketProducts ?? [],
    rebates: quote?.rebates ?? [],
    cashBackAmounts: quote?.cashBackAmounts ?? [],
    equipment: [equipmentEntry],
  });

export const mapDealBuilderFormValuesToDesking = (
  reviewSend: DealBuilderFormValues['reviewSend'],
  savedFormData: FinanceProgramFormValues,
  equipmentId: string
): FinanceProgramFormValues => {
  const { sendVia, sendEmailTo, ccEmails, notesToBuyer } = reviewSend;
  const sendToContact = sendEmailTo?.[0];

  return {
    ...savedFormData,
    contact: {
      ...savedFormData.contact,
      phone: sendToContact?.phone ?? '',
      email: sendToContact?.email ?? '',
      title: sendToContact?.title ?? '',
      fullName: sendToContact?.fullName ?? '',
    },
    equipment: {
      ...savedFormData.equipment,
      selectedEquipment: equipmentId,
    },
    sendVia: { type: sendVia as FinanceProgramFormValues['sendVia']['type'] },
    ccEmail: (ccEmails ?? []).map(o => ({
      fullName: o.fullName,
      email: o.email,
      title: o.title,
      group: o.group as string as CcEmailOptionGroups,
    })),
    notesToBuyer: notesToBuyer ?? undefined,
  };
};

export const dealSummaryGetEquipmentTitle = (
  item: QuoteEquipmentResponse | QuoteEquipmentTradeInsResponse,
  equipmentData: Nullable<DealBuilderEquipmentSource[]>
): string => {
  const source = equipmentData?.find(e => e.equipmentId === item.equipmentId);
  const title = [source?.year, source?.make, source?.model].filter(Boolean).join(' ');
  return title || (item.stockNumber ?? item.vin ?? item.equipmentId);
};

export const getAcceptedProductsByCategory = (
  snapshot: Nullable<AcceptedPaymentCriteriaSnapshot>,
  category: AftermarketProductCategory
): AcceptedMenuProduct[] =>
  (snapshot?.menuOption?.products ?? []).filter(product => product.aftermarketProduct?.productCategory === category);
